import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { Database } from "@wheels-crm/shared-types";

type ReminderRow = Database["public"]["Tables"]["reminders"]["Row"];
type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ClientRow = Database["public"]["Tables"]["clients"]["Row"];

export type ReminderWithOrder = ReminderRow & {
  order: OrderRow & { client: ClientRow };
};

const SELECT = "*, order:orders(*, client:clients(*))";

/** Powers the Reminders page (§6.5) and the Dashboard's "upcoming" widget (§6.7). */
export function useReminders() {
  const [reminders, setReminders] = useState<ReminderWithOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("reminders")
      .select(SELECT)
      .order("due_at", { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setError(null);
      setReminders((data ?? []) as unknown as ReminderWithOrder[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();

    const channel = supabase
      .channel("reminders-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "reminders" }, () => refetch())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const markDone = useCallback(async (id: string) => {
    await supabase.from("reminders").update({ is_done: true }).eq("id", id);
  }, []);

  return { reminders, loading, error, refetch, markDone };
}
